import { ThemeEvents } from '@theme/events';

/**
 * Keeps the saya size chart (snippets/saya-fit.liquid) highlighting the size
 * the shopper picks. Each row is tagged with the size range it covers in millimetres
 * (from = to for a single size).
 *
 * Listens on the section's variant:update event rather than re-rendering: the
 * variant picker only morphs itself on a size change, so the chart would
 * otherwise keep highlighting the size the page loaded on.
 */
class SayaFit extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const section = this.closest('.shopify-section') ?? document;
    section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @param {CustomEvent & {detail: {resource?: {title?: string}}}} event */
  #onVariantUpdate = (event) => {
    const title = event.detail?.resource?.title;
    if (typeof title !== 'string') return;

    // First number of the size: '8.25" (210mm)' -> 210, '(210-240mm)' -> 210.
    const match = title.match(/(\d+(?:\.\d+)?)\s*(?:-\s*\d+(?:\.\d+)?\s*)?mm/i);
    this.select(match ? Number(match[1]) : NaN);
  };

  /**
   * Highlights the row whose size, or size range, holds the given size.
   * @param {number} mm
   */
  select(mm) {
    for (const row of this.querySelectorAll('tr[data-size-from]')) {
      if (!(row instanceof HTMLElement)) continue;
      const from = Number(row.dataset.sizeFrom);
      const to = Number(row.dataset.sizeTo);
      row.toggleAttribute('data-selected', mm >= from && mm <= to);
    }
  }
}

if (!customElements.get('saya-fit')) {
  customElements.define('saya-fit', SayaFit);
}
